import type { InputProps } from '@breetometer/components';
import type { IDropdownButton } from 'BreetComponents';
import type { DropdownOptionType, IPagination } from 'BreetHelpers';

import { type ITable } from '../Table';

export type TableHeaderFilterItemOptions = DropdownOptionType & Record<string, string | number | boolean>;
export interface TableHeaderFilterListType {
	id: string;
	label: string;
	placeholder?: string;
	itemOptions: TableHeaderFilterItemOptions[];
}

export interface ISearchProps extends Omit<InputProps, 'id' | 'label'> {
	handleReqSearch?: (data: Record<string, string>) => void;
	placeHolder?: string;
	searchKey?: string;
}

export interface IDataTableHeader {
	filterList?: TableHeaderFilterListType[];
	onSelectedFilterChange?: (selectedItems?: Record<string, DropdownOptionType | undefined>) => void;
	filterProps?: IDropdownButton;
	exportProps?: IDropdownButton;
	searchProps?: ISearchProps;
	pageTitle?: string;
	handleReqSearch?: (data: Record<string, string>) => void;
	isLoading?: boolean;
	onRefresh?: () => void;
	isFilter?: boolean;
	tip?: string;
}

export interface IDataTable<E> extends ITable<E> {
	isSearchFilter?: boolean;
	className?: string;
	tip?: string;
	paginate?: {
		prevPage: number | null;
		nextPage: number | null;
		hasPrevPage: boolean;
		hasNextPage: boolean;
		pages: number;
		page: number;
		totalDocs: number;
	};
	onPageChange?: (state: Partial<IPagination>) => void;
	docItemsIdentifier?: string;
	onRefresh?: () => void;

	headerProps?: IDataTableHeader;
}

/**
 * Reshapes the selected filters object by extracting the 'value' property from each DropdownOptionType.
 *
 * @param {Record<string, DropdownOptionType>} [selectedFilters] - The object containing selected filters.
 * @returns A new object with the same keys but values replaced by their 'value' property.
 */
export const reshapeSelectedFilters = (selectedFilters?: Record<string, DropdownOptionType>) => {
	if (!selectedFilters) return {};

	return Object.keys(selectedFilters).reduce<Record<string, string>>((acc, key) => {
		acc[key] = selectedFilters[key].value as string;
		return acc;
	}, {});
};

/**
 * Filters out entries with undefined or empty string values from the input object.
 *
 * @param {Record<string, string | undefined>} input - The object to be filtered.
 * @returns A new object containing only the entries with non-empty string values.
 */
export const sanitizeObjectEntries = (input?: Record<string, unknown>) =>
	Object.entries(input ?? {}).reduce<Record<string, unknown>>((acc, [key, value]) => {
		if (value === undefined) return acc;
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (trimmed === '') return acc;
			acc[key] = trimmed;
			return acc;
		}
		return { ...acc, [key]: value };
	}, {});

/**
 * Generates filter data with the given options.
 *
 * @param {string} id - The ID of the filter category.
 * @param {string} label - The label of the filter category.
 * @param {TableHeaderFilterItemOptions[]} itemOptions - The options available in the filter category.
 * @returns - Array of filter data objects.
 */
export const createFilterData = (
	id: string,
	label: string,
	itemOptions: TableHeaderFilterItemOptions[],
	placeholder?: string
) => ({
	id,
	label,
	itemOptions,
	placeholder,
});

export const sanitizeAndCheckEmpty = (input?: Record<string, unknown>, emptyMessage = ''): string => {
	const sanitized = sanitizeObjectEntries(input);

	return Object.keys(sanitized).length === 0 ? emptyMessage : JSON.stringify(sanitized);
};
